import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import * as _ from 'lodash';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  takeUntil,
  tap
} from 'rxjs/operators';

import { User, UserList } from '@core/services/user';
import { UserService } from '@core/services/user.service';
import { ContactSummary } from '@features/contacts/models/contactsummary';
import {
  ContactSearchFilterOptions,
  ContactService
} from '@features/contacts/services/contact.service';
import {
  commandEvent,
  CommandEvent
} from '@shared/components/load/command/command.component';
import { SortOption } from '@shared/models/sort-option';

export enum DisplayState {
  Selection,
  Confirmation
}

interface Load {
  assignableUsers: UserList;
  contacts: ContactSummary[];
  otherUsers: User[];
}

@Component({
  selector: 'spark-contacts-reassign',
  templateUrl: './reassign.component.html',
  styleUrls: ['./reassign.component.scss']
})
export class ContactReassignComponent implements OnInit, OnDestroy {
  @Input() cancel: () => void = () => {};
  @Input() saved: () => void = () => {};

  assignableUsers!: UserList;
  contacts!: ContactSummary[];
  otherUsers!: User[];

  public displayState!: DisplayState;
  public DisplayState = DisplayState;

  public form!: UntypedFormGroup;

  selectedSortOption: SortOption | null = null;
  defaultFilterOptions: ContactSearchFilterOptions = {
    search: '',
    ownedBy: '',
    tagId: ''
  };
  filterForm = this.fb.group({
    filter: [this.defaultFilterOptions]
  });
  public selectedList: ContactSummary[] = [];
  private selectedContacts: Array<ContactSummary> = [];

  public initialise$!: Observable<Load>;

  private search$ = new BehaviorSubject<ContactSearchFilterOptions>(
    this.defaultFilterOptions
  );
  private destroying$ = new Subject<void>();
  private save$: Subject<null> = new Subject<null>();
  private savePayload$: Observable<string> = this.save$.pipe(
    map(() => this.reassignTo.value)
  );
  public persist$: Observable<CommandEvent<object>> = commandEvent(
    this.savePayload$,
    (portfolioId) =>
      this.contactService.reassignAll(this.selectedContacts, portfolioId)
  );

  constructor(
    private fb: UntypedFormBuilder,
    private contactService: ContactService,
    private userService: UserService
  ) {
    // set default state
    this.displayState = DisplayState.Selection;
  }

  ngOnInit(): void {
    this.constructForm();
    this.subscribeToFilterForms();

    this.initialise$ = combineLatest([
      this.userService.getAssignableUsers(),
      this.search$
    ]).pipe(
      switchMap(([assignableUsers, filters]) => {
        let myPortfolio = this.userService.getMyPortfolio(assignableUsers);

        this.defaultFilterOptions.ownedBy = myPortfolio;

        return this.contactService.getContacts(filters).pipe(
          map((contacts) => ({
            assignableUsers,
            contacts,
            otherUsers: this.userService.getOtherUsers(assignableUsers)
          })),
          tap(({ assignableUsers, contacts, otherUsers }) => {
            this.assignableUsers = assignableUsers;
            this.contacts = contacts;
            this.otherUsers = otherUsers;
          })
        );
      })
    );
  }

  private subscribeToFilterForms() {
    this.filterForm.valueChanges
      .pipe(
        takeUntil(this.destroying$),
        map((v) => v.filter),
        debounceTime(500),
        distinctUntilChanged((x, y) => _.isEqual(x, y))
      )
      .subscribe((v) => {
        this.search$.next(v);
      });
  }

  ngOnDestroy(): void {
    this.destroying$.next(undefined);
    this.destroying$.complete();
  }

  private constructForm(): void {
    this.form = this.fb.group({
      reassignTo: new UntypedFormControl(null, Validators.required)
    });
  }

  onSortSelected(sortOption: SortOption | null) {
    this.selectedSortOption = sortOption;
  }

  public get reassignTo(): UntypedFormControl {
    return this.form.get('reassignTo') as UntypedFormControl;
  }

  public get determineModalTitle(): string {
    return this.displayState === DisplayState.Selection
      ? 'Reassign'
      : 'Confirmation';
  }

  public get determinePrimaryBtnLabel(): string {
    return this.displayState === DisplayState.Selection ? 'Next' : 'Confirm';
  }

  public get determineSecondaryBtnLabel(): string {
    return this.displayState === DisplayState.Selection ? 'Close' : 'Back';
  }

  public get determinePrimaryBtnState(): boolean {
    if (this.displayState === DisplayState.Selection) {
      return !this.isFormValid();
    }
    return false;
  }

  private isFormValid(): boolean {
    return this.form.valid && this.selectedContacts.length > 0;
  }

  public onClickPrimaryAction(): void {
    switch (this.displayState) {
      case DisplayState.Selection:
        if (this.isFormValid()) {
          this.selectedList = [];
          this.contacts.forEach((contact) => {
            if (this.selectedContacts.find((s) => s.id == contact.id)) {
              this.selectedList.push(contact);
            }
          });

          this.displayState = DisplayState.Confirmation;
        }
        break;

      case DisplayState.Confirmation:
        this.save$.next(null);
        break;
    }
  }

  public onClickSecondaryAction(): void {
    switch (this.displayState) {
      case DisplayState.Selection:
        this.cancel(); // close modal
        break;

      case DisplayState.Confirmation:
        this.displayState = DisplayState.Selection;
        break;
    }
  }

  public isContactSelected(contact: ContactSummary): boolean {
    return this.selectedContacts.find((v) => v.id === contact.id)
      ? true
      : false;
  }

  public toggleAllContacts(isChecked: boolean): void {
    if (isChecked) {
      this.selectedContacts.push(...this.contacts);
      return;
    }

    this.selectedContacts = [];
  }

  public onToggleContact(isChecked: boolean, contact: ContactSummary): void {
    // return if already exists
    if (this.selectedContacts.find((c) => c.id == contact.id) && isChecked) {
      return;
    }

    if (isChecked) {
      this.selectedContacts.push(contact);
    } else {
      this.selectedContacts = this.selectedContacts.filter(
        (v) => v.id !== contact.id
      );
    }
  }
}
